import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Eye, MapPin, Clock, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * FOMO ticker — "Next drop: <city> — <label>. N hunters already watching."
 * Shows above the daily-quote banner on the home page. Hides itself when
 * the admin toggles the feature off or when no teaser is configured.
 */
export default function TeaserTicker() {
  const [t, setT] = useState(null);
  const [enabled, setEnabled] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [watched, setWatched] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/teaser");
        if (!alive) return;
        if (data.enabled === false) { setEnabled(false); return; }
        setT(data.teaser);
      } catch { /* silent */ }
      finally { if (alive) setLoaded(true); }
    })();
    return () => { alive = false; };
  }, []);

  const watch = async () => {
    if (!t || watched) return;
    try {
      const { data } = await api.post(`/teaser/${t.id}/watch`);
      setT((prev) => ({ ...prev, watchers_count: data.watchers_count }));
      setWatched(true);
      toast.success("You're watching — we'll ping you when the drop goes live.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Try again");
    }
  };

  if (!loaded) return null;
  if (!enabled || !t) return null;

  return (
    <section
      className="relative overflow-hidden rounded-[2px] border flex items-center"
      style={{
        borderColor: "#E53935",
        background: "linear-gradient(90deg, rgba(229,57,53,0.12) 0%, rgba(15,15,15,1) 60%)",
        boxShadow: "0 0 0 1px rgba(229,57,53,0.5), 0 0 24px -6px rgba(229,57,53,0.3)",
      }}
      data-testid="teaser-ticker"
    >
      <div
        aria-hidden
        className="shrink-0 px-3 md:px-4 py-3 font-mono text-[10px] md:text-xs uppercase tracking-widest flex items-center gap-1"
        style={{ color: "#0F0F0F", background: "#E53935", letterSpacing: "0.24em" }}
      >
        <Flame size={12} className="animate-pulse" /> Next Drop
      </div>

      <div className="flex-1 min-w-0 px-4 md:px-6 py-3 flex items-center justify-between gap-4 flex-wrap md:flex-nowrap">
        <div className="min-w-0 leading-tight">
          <div className="font-display uppercase text-base md:text-xl text-[#F5F2E8] truncate" data-testid="teaser-line">
            <span style={{ color: "#D7A62A" }} className="inline-flex items-center gap-1">
              <MapPin size={14} /> {t.city}
            </span>
            <span className="text-[#F5F2E8]/40 mx-2">—</span>
            <span className="inline-flex items-center gap-1">
              <Clock size={12} /> {t.label}
            </span>
          </div>
          <div className="font-mono text-[11px] md:text-xs text-[#F5F2E8]/60 mt-0.5" data-testid="teaser-count">
            <span style={{ color: "#76B82A" }}>{t.watchers_count}</span>{" "}
            hunter{t.watchers_count === 1 ? "" : "s"} already watching
          </div>
        </div>

        <button
          onClick={watch}
          disabled={watched}
          className="btn-gold text-xs inline-flex items-center gap-2 shrink-0"
          data-testid="teaser-watch-btn"
        >
          <Eye size={12} />
          {watched ? "You're in" : "I'm watching"}
        </button>
      </div>
    </section>
  );
}
